import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';

import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import { CopyCodeComponent } from './CopyCodeModal';
import { Copy as CopyIcon, Rocket, Globe, Smartphone, Trash2, AlertTriangle, AppWindow } from 'lucide-react';
import useApi from '../api';
import useAnalytics from '../analytics/analytics';
import { getDeleteErrorMessage } from './deleteAgent';
import AgentTabHeader from './tabs/AgentTabHeader';


interface DeployAgentProps {
    agId: string;
    agentName?: string;
    chatbotEnabled: boolean;
    setChatbotEnabled: (chatbotEnabled: boolean) => void;
    mobileEnabled: boolean;
    setMobileEnabled: (mobileEnabled: boolean) => void;
    setNeedsUpdate: (needsUpdate: boolean) => void;
}

const DeployAgent: React.FC<DeployAgentProps> = ({ agId, agentName, chatbotEnabled, setChatbotEnabled, mobileEnabled, setMobileEnabled, setNeedsUpdate }) => {
    const { captureEvent } = useAnalytics();
    const { remove } = useApi();
    const navigate = useNavigate();
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteAgent = async () => {
        setIsDeleting(true);
        try {
            await remove(`/agents/${agId}`);
            captureEvent('delete_agent', { agent_id: agId });
            toast.success('Agent deleted successfully', toastConfig);
            navigate('/agents');
        } catch (error) {
            console.error('Error deleting Agent:', error);
            toast.error(getDeleteErrorMessage(error), toastConfig);
            setIsDeleting(false);
        }
    };

    const getWidgetBaseUrl = () => {
        return import.meta.env.VITE_WIDGET_URL;
    };

    const iframeSrc = `${getWidgetBaseUrl()}/agent/${agId}`;
    const iframeSnippet = `<iframe\n  src="${iframeSrc}"\n  width="100%"\n  style="height: 100%"\n></iframe>`;

    const copyIframeSnippet = async () => {
        try {
            await navigator.clipboard.writeText(iframeSnippet);
            toast.success('Iframe embed code copied', toastConfig);
        } catch (err) {
            toast.error('Failed to copy code', toastConfig);
        }
    };

    const sdkInstallSnippet = 'npm install @aimdoc/sdk @aimdoc/sdk-react';
    const sdkProviderSnippet = `import { AimdocProvider } from '@aimdoc/sdk-react';

<AimdocProvider agentId="${agId}">
  <App />
</AimdocProvider>`;
    const sdkIdentifySnippet = `import { aimdoc } from '@aimdoc/sdk';

// After sign-in: tell the agent who it is talking to.
aimdoc.identify({
  external_id: user.id,
  email: user.email,
  first_name: user.firstName,
  account: { external_id: account.id, name: account.name },
  user_hash: hashFromYourServer, // identity verification (recommended)
});`;

    const copySdkSnippet = (snippet: string, label: string) => async () => {
        try {
            await navigator.clipboard.writeText(snippet);
            toast.success(`${label} copied`, toastConfig);
            captureEvent('copy_sdk_snippet', { agent_id: agId, snippet: label });
        } catch (err) {
            toast.error('Failed to copy code', toastConfig);
        }
    };

    const handleCopyEmbedCode = (platform: string) => {
        toast.success('Code copied to clipboard', toastConfig);
        captureEvent('copy_embed_code', {
            agent_id: agId,
            platform: platform
        });
    };

    return (
        <div className="w-full">
            <AgentTabHeader
                icon={Rocket}
                title="Deploy"
                description="Configure visibility and install your agent on a website or page."
            />

            <div className="mx-auto w-full max-w-4xl space-y-8 py-6">
            {/* Agent Visibility Section */}
            <div className="space-y-3">
                <div>
                    <h3 className="text-sm font-semibold">Visibility</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Control where the published agent is available.
                    </p>
                </div>

                <div className="overflow-hidden rounded-lg border bg-card divide-y">
                    <div className="flex items-center justify-between gap-6 p-4">
                        <div className="flex items-start gap-3">
                            <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <div className="space-y-1">
                                <Label htmlFor="agent-status" className="text-sm font-medium cursor-pointer">
                                    Website visibility
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                    Enable or disable the agent on your website. Publish to make changes live.
                                </p>
                            </div>
                        </div>
                        <Switch
                            id="agent-status"
                            checked={chatbotEnabled}
                            onCheckedChange={(checked) => {
                                setChatbotEnabled(checked);
                                setNeedsUpdate(true);
                            }}
                        />
                    </div>

                    <div className="flex items-center justify-between gap-6 p-4">
                        <div className="flex items-start gap-3">
                            <Smartphone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <div className="space-y-1">
                                <Label htmlFor="mobile-visibility" className="text-sm font-medium cursor-pointer">
                                    Mobile visibility
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                    Show or hide the agent for visitors on mobile devices. Publish to make changes live.
                                </p>
                            </div>
                        </div>
                        <Switch
                            id="mobile-visibility"
                            checked={mobileEnabled}
                            onCheckedChange={(checked) => {
                                setMobileEnabled(checked);
                                setNeedsUpdate(true);
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Embed Code Section */}
            <div className="space-y-3">
                <div>
                    <h3 className="text-sm font-semibold">Widget embed</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Choose your platform and copy the installation code.
                    </p>
                </div>

                <div className="rounded-lg border bg-card p-4">
                    <CopyCodeComponent agId={agId} onCopy={handleCopyEmbedCode} />
                </div>
            </div>

            {/* Iframe Embed Section */}
            <div className="space-y-3">
                <div>
                    <h3 className="text-sm font-semibold">Iframe embed</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Embed the agent directly inside any page.
                    </p>
                </div>

                <div className="rounded-lg border bg-card p-4">
                    <div className="overflow-hidden rounded-lg border bg-muted/20">
                        <div className="flex items-center justify-between border-b px-3 py-2">
                            <span className="text-xs text-muted-foreground">iframe.html</span>
                            <Button onClick={copyIframeSnippet} variant="ghost" size="sm" className="h-7 px-2 text-xs">
                                <CopyIcon className="mr-1 h-3 w-3" />
                                Copy
                            </Button>
                        </div>
                        <pre className="overflow-x-auto bg-background/50 p-4">
                            <code className="font-mono text-xs text-foreground/90">{iframeSnippet.trim()}</code>
                        </pre>
                    </div>
                </div>
            </div>

            {/* App SDK Section */}
            <div className="space-y-3">
                <div>
                    <div className="flex items-center gap-2">
                        <AppWindow className="h-3.5 w-3.5 text-muted-foreground" />
                        <h3 className="text-sm font-semibold">In your application (SDK)</h3>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Run the agent inside your product for signed-in users. App-mode behavior
                        (prompt, tools, appearance) is configured on the Application tab of this
                        agent's settings.
                    </p>
                </div>

                <div className="space-y-3 rounded-lg border bg-card p-4">
                    {[
                        { file: 'terminal', label: 'Install command', snippet: sdkInstallSnippet },
                        { file: 'App.tsx', label: 'Provider snippet', snippet: sdkProviderSnippet },
                        { file: 'auth.ts', label: 'Identify snippet', snippet: sdkIdentifySnippet },
                    ].map(({ file, label, snippet }) => (
                        <div key={file} className="overflow-hidden rounded-lg border bg-muted/20">
                            <div className="flex items-center justify-between border-b px-3 py-2">
                                <span className="text-xs text-muted-foreground">{file}</span>
                                <Button onClick={copySdkSnippet(snippet, label)} variant="ghost" size="sm" className="h-7 px-2 text-xs">
                                    <CopyIcon className="mr-1 h-3 w-3" />
                                    Copy
                                </Button>
                            </div>
                            <pre className="overflow-x-auto bg-background/50 p-4">
                                <code className="font-mono text-xs text-foreground/90">{snippet}</code>
                            </pre>
                        </div>
                    ))}
                    <p className="text-xs text-muted-foreground">
                        Identity verification signs the identify payload with your organization's
                        secret so the agent can trust who it is talking to — see Settings →
                        Identity verification for the server-side hash.
                    </p>
                </div>
            </div>

            {/* Danger Zone */}
            <div className="space-y-3">
                <div>
                    <h3 className="text-sm font-semibold text-destructive">Danger zone</h3>
                </div>

                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                            <div className="space-y-1">
                                <p className="text-sm font-medium">Delete this agent</p>
                                <p className="text-xs text-muted-foreground">
                                    Permanently remove this agent and its configuration. This action cannot be undone.
                                </p>
                            </div>
                        </div>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button variant="destructive" size="sm" className="shrink-0" disabled={isDeleting}>
                                    <Trash2 className="h-4 w-4 mr-1.5" />
                                    Delete Agent
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Delete {agentName || 'this agent'}?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        This permanently deletes the agent and its configuration. This action cannot be undone.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleDeleteAgent();
                                        }}
                                        disabled={isDeleting}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                        {isDeleting ? 'Deleting…' : 'Delete Agent'}
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
}

export default DeployAgent;
